import React, { useState, useMemo } from "react";
import {
  MDBRow,
  MDBCard,
  MDBCardBody,
  MDBCol,
  MDBIcon,
  MDBTabs,
  MDBTabsItem,
  MDBTabsLink,
  MDBTooltip
} from "mdb-react-ui-kit";
import CommonViewLayout from "../CommonViewLayout";
import ComposeMessage from "./ComposeMessage";
import MessageList from "./MessageList";
import MessageFilters from "./MessageFilters";

const Messages = () => {
  const [showComposer, setShowComposer] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [activeTab, setActiveTab] = useState("messages");
  const [currentPage, setCurrentPage] = useState(1);
  // Separate view type states for each section with their own defaults
  const [messagesViewType, setMessagesViewType] = useState("card");
  const [managementViewType, setManagementViewType] = useState("table");
  const itemsPerPage = 5;
  const [showManagementTab, setShowManagementTab] = useState(true);

  // Separate message datasets with HTML content
  const [userMessages] = useState([
    {
      id: 1,
      subject: "Welcome to the Portal",
      content: `<div>
        <h4>Welcome to Our Portal!</h4>
        <p>We're excited to have you join our platform. Here are some key features you should know about:</p>
        <ul>
          <li>Personalized dashboard with real-time updates</li>
          <li>Advanced search and filtering capabilities</li>
          <li>Integrated messaging system</li>
          <li>Customizable notifications</li>
        </ul>
        <p>Take some time to explore these features and let us know if you have any questions!</p>
        <p><strong>Getting Started:</strong></p>
        <ol>
          <li>Complete your profile</li>
          <li>Set your preferences</li>
          <li>Connect with team members</li>
        </ol>
      </div>`,
      status: "present",
      createdAt: "2024-01-15 to 2024-01-16",
      author: "pbs_apelar"
    },
    {
      id: 4,
      subject: "Holiday Schedule Update",
      content: `<div>
        <h4>Updated Holiday Schedule 2024</h4>
        <p>Please note the following holidays for the upcoming quarter:</p>
        <ul>
          <li>Presidents' Day - February 19</li>
          <li>Memorial Day - May 27</li>
          <li>Independence Day - July 4</li>
        </ul>
        <p>Plan your work schedules accordingly.</p>
      </div>`,
      status: "future",
      createdAt: "2024-02-01 to 2024-02-02",
      author: "ben"
    },
    {
      id: 5,
      subject: "Office Safety Guidelines",
      content: `<div>
        <h4>Important Safety Reminders</h4>
        <p>For everyone's safety, please follow these guidelines:</p>
        <ul>
          <li>Keep emergency exits clear</li>
          <li>Report any safety concerns immediately</li>
          <li>Follow proper equipment handling procedures</li>
        </ul>
      </div>`,
      status: "present",
      createdAt: "2024-01-20 to 2024-01-21",
      author: "stephen"
    },
    {
      id: 6,
      subject: "IT System Updates",
      content: `<div>
        <h4>Upcoming System Improvements</h4>
        <p>The following updates will be implemented:</p>
        <ul>
          <li>Enhanced security protocols</li>
          <li>Faster database performance</li>
          <li>New backup systems</li>
        </ul>
      </div>`,
      status: "future",
      createdAt: "2024-02-15 to 2024-02-16",
      author: "amanda"
    },
    {
      id: 7,
      subject: "Employee Training",
      content: `<div>
        <h4>Mandatory Training Sessions</h4>
        <p>All employees must complete:</p>
        <ul>
          <li>Cybersecurity Awareness</li>
          <li>Workplace Ethics</li>
          <li>Time Management</li>
        </ul>
        <p>Deadline: March 1, 2024</p>
      </div>`,
      status: "present",
      createdAt: "2024-01-25 to 2024-01-26",
      author: "ravinder"
    },
    {
      id: 8,
      subject: "Office Renovation",
      content: `<div>
        <h4>Building Updates</h4>
        <p>The following areas will be renovated:</p>
        <ul>
          <li>Main lobby - February 1-5</li>
          <li>Conference rooms - February 6-10</li>
          <li>Break room - February 11-15</li>
        </ul>
      </div>`,
      status: "future",
      createdAt: "2024-01-28 to 2024-01-29",
      author: "pbs_apelar"
    },
    {
      id: 9,
      subject: "Team Building Event",
      content: `<div>
        <h4>Annual Team Building Day</h4>
        <p>Join us for a day of:</p>
        <ul>
          <li>Team activities</li>
          <li>Professional development</li>
          <li>Networking opportunities</li>
        </ul>
        <p>Date: March 15, 2024</p>
      </div>`,
      status: "future",
      createdAt: "2024-02-20 to 2024-02-21",
      author: "ben"
    },
    {
      id: 10,
      subject: "Policy Updates",
      content: `<div>
        <h4>Remote Work Policy Changes</h4>
        <p>New guidelines for remote work:</p>
        <ul>
          <li>Flexible hours implementation</li>
          <li>Equipment requirements</li>
          <li>Communication protocols</li>
        </ul>
      </div>`,
      status: "present",
      createdAt: "2024-01-30 to 2024-01-31",
      author: "stephen"
    },
    {
      id: 11,
      subject: "Customer Service Update",
      content: `<div>
        <h4>New Service Standards</h4>
        <p>Updated customer service protocols:</p>
        <ul>
          <li>Response time requirements</li>
          <li>Quality assurance measures</li>
          <li>Customer feedback integration</li>
        </ul>
      </div>`,
      status: "past",
      createdAt: "2024-01-05 to 2024-01-06",
      author: "amanda"
    },
    {
      id: 12,
      subject: "Environmental Initiative",
      content: `<div>
        <h4>Going Green Campaign</h4>
        <p>New office sustainability measures:</p>
        <ul>
          <li>Paperless documentation</li>
          <li>Energy conservation</li>
          <li>Recycling program updates</li>
        </ul>
      </div>`,
      status: "present",
      createdAt: "2024-01-22 to 2024-01-23",
      author: "ravinder"
    },
    {
      id: 13,
      subject: "Project Deadlines",
      content: `<div>
        <h4>Q1 Project Timeline Updates</h4>
        <p>Important upcoming deadlines:</p>
        <ul>
          <li>Phase 1 completion - February 28</li>
          <li>Mid-project review - March 15</li>
          <li>Final deliverables - March 31</li>
        </ul>
      </div>`,
      status: "future",
      createdAt: "2024-02-10 to 2024-02-11",
      author: "pbs_apelar"
    },
    {
      id: 2,
      subject: "System Maintenance Notice",
      content: `<div>
        <h4>Scheduled System Maintenance</h4>
        <p>We will be performing essential system maintenance to improve our services. During this time, some features may be temporarily unavailable.</p>
        <p><strong>Maintenance Details:</strong></p>
        <ul>
          <li>Date: January 20th, 2024</li>
          <li>Time: 2:00 AM - 4:00 AM EST</li>
          <li>Duration: Approximately 2 hours</li>
        </ul>
        <p><em>Impact on Services:</em></p>
        <ul>
          <li>User authentication system</li>
          <li>Database operations</li>
          <li>File upload/download services</li>
        </ul>
        <p>We apologize for any inconvenience this may cause. For urgent matters during the maintenance window, please contact our support team.</p>
      </div>`,
      status: "future",
      createdAt: "2024-01-14 to 2024-01-15",
      author: "ben"
    },
    {
      id: 3,
      subject: "New Features Available",
      content: `<div>
        <h4>Exciting New Features Released!</h4>
        <p>We're thrilled to announce the launch of several new features designed to enhance your experience:</p>
        <h5>1. Advanced Analytics Dashboard</h5>
        <ul>
          <li>Real-time data visualization</li>
          <li>Custom report generation</li>
          <li>Export capabilities in multiple formats</li>
        </ul>
        <h5>2. Enhanced Security Features</h5>
        <ul>
          <li>Two-factor authentication</li>
          <li>Improved password policies</li>
          <li>Session management tools</li>
        </ul>
        <p>These updates are now live and ready for you to explore. Check out our <a href="#">documentation</a> for more details.</p>
      </div>`,
      status: "past",
      createdAt: "2024-01-13 to 2024-01-14",
      author: "stephen"
    }
  ]);

  const [managementMessages] = useState([
    {
      id: 101,
      subject: "Internal Review Schedule",
      content: `<div>
        <h4>Quarterly Review Process Update</h4>
        <p>Important changes to our quarterly review process have been implemented:</p>
        <ol>
          <li><strong>New Timeline:</strong>
            <ul>
              <li>Self-assessment period: First week</li>
              <li>Manager review: Second week</li>
              <li>1:1 Meetings: Third week</li>
              <li>Final documentation: Fourth week</li>
            </ul>
          </li>
          <li><strong>Updated Forms:</strong>
            <ul>
              <li>Performance metrics template</li>
              <li>Goal-setting worksheet</li>
              <li>Feedback collection form</li>
            </ul>
          </li>
        </ol>
        <p>Please ensure all team members are familiar with these changes before the next review cycle.</p>
      </div>`,
      status: "future",
      createdAt: "2024-01-10 to 2024-01-11",
      author: "amanda"
    },
    {
      id: 102,
      subject: "Policy Update",
      content: `<div>
        <h4>Updated Privacy Policy Guidelines</h4>
        <p>We've revised our internal privacy policy to better protect sensitive information:</p>
        <h5>Key Changes:</h5>
        <ul>
          <li>Enhanced data handling procedures</li>
          <li>Updated access control protocols</li>
          <li>New data retention guidelines</li>
        </ul>
        <p><strong>Important Deadlines:</strong></p>
        <ul>
          <li>Policy review completion: January 25th</li>
          <li>Acknowledgment submission: January 30th</li>
          <li>New procedures implementation: February 1st</li>
        </ul>
        <p>Please review these changes carefully and reach out with any questions.</p>
      </div>`,
      status: "present",
      createdAt: "2024-01-09 to 2024-01-10",
      author: "ravinder"
    },
    {
      id: 103,
      subject: "Team Guidelines",
      content: `<div>
        <h4>Updated Team Guidelines and Best Practices</h4>
        <p>To ensure consistent quality and efficiency across all departments, we've updated our team guidelines:</p>
        <h5>Communication Standards:</h5>
        <ul>
          <li>Use appropriate channels for different types of communication</li>
          <li>Maintain professional tone in all interactions</li>
          <li>Follow response time expectations</li>
        </ul>
        <h5>Project Management:</h5>
        <ul>
          <li>Regular status updates</li>
          <li>Resource allocation procedures</li>
          <li>Timeline management</li>
        </ul>
        <p>These guidelines are effective immediately. Training sessions will be scheduled soon.</p>
      </div>`,
      status: "past",
      createdAt: "2024-01-08 to 2024-01-09",
      author: "pbs_apelar"
    },
    {
      id: 104,
      subject: "Budget Planning 2024",
      content: `<div>
        <h4>Annual Budget Review and Planning</h4>
        <p>Key budget planning points for the upcoming fiscal year:</p>
        <ul>
          <li>Department budget submissions due: March 1</li>
          <li>Budget review meetings: March 15-20</li>
          <li>Final approvals: March 31</li>
        </ul>
        <p><strong>Required Documentation:</strong></p>
        <ul>
          <li>Previous year analysis</li>
          <li>Projected expenses</li>
          <li>Resource allocation plans</li>
        </ul>
      </div>`,
      status: "future",
      createdAt: "2024-02-15 to 2024-02-16",
      author: "ben"
    },
    {
      id: 105,
      subject: "Staff Performance Reviews",
      content: `<div>
        <h4>Q1 Performance Review Guidelines</h4>
        <p>Updated performance review process:</p>
        <ul>
          <li>360-degree feedback implementation</li>
          <li>New KPI metrics</li>
          <li>Career development planning</li>
        </ul>
        <p>All managers must complete reviews by March 30.</p>
      </div>`,
      status: "present",
      createdAt: "2024-01-25 to 2024-01-26",
      author: "stephen"
    },
    {
      id: 106,
      subject: "Department Restructuring",
      content: `<div>
        <h4>Organizational Changes</h4>
        <p>Upcoming department restructuring details:</p>
        <ul>
          <li>New reporting hierarchies</li>
          <li>Team consolidation plans</li>
          <li>Role realignment</li>
        </ul>
        <p>Effective date: April 1, 2024</p>
      </div>`,
      status: "future",
      createdAt: "2024-02-20 to 2024-02-21",
      author: "amanda"
    },
    {
      id: 107,
      subject: "Quality Assurance Updates",
      content: `<div>
        <h4>New QA Protocols</h4>
        <p>Implementation of enhanced quality measures:</p>
        <ul>
          <li>Updated testing procedures</li>
          <li>Documentation requirements</li>
          <li>Compliance checkpoints</li>
        </ul>
        <p>Training sessions scheduled for March 5-7.</p>
      </div>`,
      status: "present",
      createdAt: "2024-01-28 to 2024-01-29",
      author: "ravinder"
    },
    {
      id: 108,
      subject: "Strategic Planning Session",
      content: `<div>
        <h4>2024 Strategy Meeting</h4>
        <p>Annual strategy planning session details:</p>
        <ul>
          <li>Market analysis review</li>
          <li>Goal setting for Q2-Q4</li>
          <li>Resource planning</li>
        </ul>
        <p>Location: Main Conference Room</p>
        <p>Date: March 10, 2024</p>
      </div>`,
      status: "future",
      createdAt: "2024-02-25 to 2024-02-26",
      author: "pbs_apelar"
    },
    {
      id: 109,
      subject: "Compliance Training",
      content: `<div>
        <h4>Mandatory Management Training</h4>
        <p>All managers must complete:</p>
        <ul>
          <li>Ethics and compliance update</li>
          <li>Leadership development module</li>
          <li>Risk management training</li>
        </ul>
        <p>Completion deadline: February 28, 2024</p>
      </div>`,
      status: "present",
      createdAt: "2024-01-20 to 2024-01-21",
      author: "ben"
    },
    {
      id: 110,
      subject: "Resource Allocation",
      content: `<div>
        <h4>Q2 Resource Planning</h4>
        <p>Resource allocation guidelines:</p>
        <ul>
          <li>Staff distribution plans</li>
          <li>Equipment assignments</li>
          <li>Budget allocations</li>
        </ul>
        <p>Submit requirements by March 15.</p>
      </div>`,
      status: "future",
      createdAt: "2024-02-28 to 2024-02-29",
      author: "stephen"
    },
    {
      id: 111,
      subject: "Management KPIs",
      content: `<div>
        <h4>Updated Performance Metrics</h4>
        <p>New management KPI framework:</p>
        <ul>
          <li>Team productivity metrics</li>
          <li>Project success indicators</li>
          <li>Customer satisfaction goals</li>
        </ul>
        <p>Implementation date: March 1, 2024</p>
      </div>`,
      status: "present",
      createdAt: "2024-01-15 to 2024-01-16",
      author: "amanda"
    },
    {
      id: 112,
      subject: "Vendor Management",
      content: `<div>
        <h4>Vendor Review Process</h4>
        <p>Updated vendor management procedures:</p>
        <ul>
          <li>Performance evaluation criteria</li>
          <li>Contract renewal guidelines</li>
          <li>Cost optimization strategies</li>
        </ul>
        <p>Review meetings: March 20-22</p>
      </div>`,
      status: "future",
      createdAt: "2024-03-01 to 2024-03-02",
      author: "ravinder"
    },
    {
      id: 113,
      subject: "Management Reporting",
      content: `<div>
        <h4>New Reporting Requirements</h4>
        <p>Changes to management reporting structure:</p>
        <ul>
          <li>Weekly progress summaries</li>
          <li>Monthly performance dashboards</li>
          <li>Quarterly review documentation</li>
        </ul>
        <p>Effective immediately</p>
      </div>`,
      status: "present",
      createdAt: "2024-01-18 to 2024-01-19",
      author: "pbs_apelar"
    }
  ]);

  // Get the current view type based on active tab
  const getCurrentViewType = () => {
    return activeTab === "management" ? managementViewType : messagesViewType;
  };

  const activeMessages = useMemo(() => {
    const source = activeTab === "management" ? managementMessages : userMessages;
    const startIndex = (currentPage - 1) * itemsPerPage;
    return source.slice(startIndex, startIndex + itemsPerPage);
  }, [activeTab, currentPage, userMessages, managementMessages]);

  const totalPages = useMemo(() => {
    const source = activeTab === "management" ? managementMessages : userMessages;
    return Math.ceil(source.length / itemsPerPage);
  }, [activeTab, userMessages, managementMessages]);

  const handleAddMessage = () => {
    setEditingMessage(null);
    setShowComposer(true);
  };

  const handleEditMessage = (message) => {
    setEditingMessage(message);
    setShowComposer(true);
  };

  const handleDeleteMessage = (messageId) => {
    console.log("Delete message:", messageId);
    // Implement actual delete logic
  };

  const handleBackToList = () => {
    setShowComposer(false);
    setEditingMessage(null);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Updated view type change handler
  const handleViewTypeChange = (newViewType) => {
    if (activeTab === "management") {
      setManagementViewType(newViewType);
    } else {
      setMessagesViewType(newViewType);
    }
  };

  const messageFilters = useMemo(() => (
    <MessageFilters
      viewType={getCurrentViewType()}
      onViewTypeChange={handleViewTypeChange}
      onAddMessage={handleAddMessage}
    />
  ), [activeTab, messagesViewType, managementViewType]);


  if (showComposer) {
    return (
      <ComposeMessage
        onBack={handleBackToList}
        editingMessage={editingMessage}
      />
    );
  }

  return (
    <CommonViewLayout
      headerTitle="Messages"
      filters={messageFilters}
      iconName="comment"
    >
      <section>
        <MDBRow>
          <MDBCol>
            <MDBCard>
              <MDBCardBody>
                <MDBTabs className="mb-3">
                  <MDBTabsItem>
                    <MDBTooltip tag='span' title="Messages specific to your domain">
                      <MDBTabsLink
                        onClick={() => {
                          setActiveTab("messages");
                          setCurrentPage(1);
                        }}
                        active={activeTab === "messages"}
                        style={{ cursor: "pointer" }}
                      >
                        <MDBIcon icon="comment" className="me-2" />
                        Messages
                      </MDBTabsLink>
                    </MDBTooltip>
                  </MDBTabsItem>

                  {showManagementTab && (
                    <MDBTabsItem>
                      <MDBTooltip tag='span' title="Global messages visible across all domains">
                        <MDBTabsLink
                          onClick={() => {
                            setActiveTab("management");
                            setCurrentPage(1);
                          }}
                          active={activeTab === "management"}
                          style={{ cursor: "pointer" }}
                        >
                          <MDBIcon icon="cogs" className="me-2" />
                          Message Management
                        </MDBTabsLink>
                      </MDBTooltip>
                    </MDBTabsItem>
                  )}
                </MDBTabs>

                {/* Tab content */}
                <div className="tab-content">
                  <MessageList
                    messages={activeMessages}
                    viewType={getCurrentViewType()}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onEdit={handleEditMessage}
                    onDelete={handleDeleteMessage}
                    onPageChange={handlePageChange}
                    onAddMessage={handleAddMessage}
                  />
                </div>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>
      </section>
    </CommonViewLayout>
  );
};

export default Messages;
